// Signup — create a brand-new tenant workspace. Owner-account setup.
// Calls POST /api/auth/signup-tenant which returns the full session.

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { KbdChip } from '@/components/primitives/KbdChip';
import { api } from '@/lib/api';
import { cleanErr } from '@/lib/errMsg';
import { useAuthStore } from '@/store/auth';

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

export function SignupPage() {
  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [ownerName, setOwnerName]   = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [companyName, setCompanyName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [seedSampleData, setSeedSampleData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onTenantNameChange(v: string) {
    setTenantName(v);
    if (!slugTouched) setTenantSlug(slugify(v));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords don\'t match.'); return; }
    if (password.length < 10) { setError('Password must be at least 10 characters.'); return; }
    setLoading(true);
    try {
      const body: Record<string, string> = {
        tenantSlug, tenantName,
        ownerEmail: ownerEmail.trim(),
        ownerName: ownerName.trim(),
        password,
      };
      if (ownerPhone.trim()) body.ownerPhone = ownerPhone.trim().startsWith('+91') ? ownerPhone.trim() : `+91${ownerPhone.trim().replace(/\D/g, '').slice(-10)}`;
      if (companyName.trim()) body.companyName = companyName.trim();
      if (seedSampleData) body.seedSampleData = 'true';
      await api.post('/auth/signup-tenant', body);
      // signup-tenant doesn't set session cookies — sign the new owner in with
      // the credentials they just typed instead of bouncing them to /login.
      await api.post('/auth/login', { email: ownerEmail.trim(), password });
      // Hydrate session
      const me = await api.get('/auth/me');
      useAuthStore.getState().setAuthenticated({
        user: me.data.user,
        tenant: me.data.tenant,
        companies: me.data.companies,
        activeCompanyId: me.data.activeCompanyId,
        activeBranchId: me.data.activeBranchId,
        supplierStateCode: me.data.supplierStateCode ?? null,
      });
    } catch (err) {
      setError(cleanErr(err, 'Network error. Try again.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-snow">
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-40 h-[640px] w-[640px] rounded-full opacity-[0.07] blur-3xl bg-cobalt" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full opacity-[0.05] blur-3xl bg-citrus" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[520px] flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <img src="/brand/mark.svg" alt="" aria-hidden className="h-10 w-10" />
          <div className="flex flex-col leading-none">
            <span className="font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>Orenza</span>
            <span className="mt-0.5 text-caption text-slate">Billing Solutions</span>
          </div>
        </div>

        <div className="w-full rounded-md border border-mist bg-frost p-8">
          <h1 className="mb-1 font-display text-display-sm font-semibold text-obsidian">Create your workspace</h1>
          <p className="mb-6 text-body-sm text-slate">Free during the internal phase. Indian Rupee · GST 2.0 ready.</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Workspace name" required value={tenantName} onChange={(e) => onTenantNameChange(e.target.value)} placeholder="e.g. Raaj Industries" autoFocus />
              <Input label="URL slug" required value={tenantSlug}
                onChange={(e) => { setSlugTouched(true); setTenantSlug(slugify(e.target.value)); }}
                placeholder="raaj-industries"
                hint="2-40 chars, lowercase letters/digits/hyphens" />
            </div>

            <Input label="Your name" required value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Hatim" autoComplete="name" />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Email" type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="you@company.in" autoComplete="email" />
              <Input label="Phone (optional)" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} placeholder="+91 …" hint="Indian mobile" autoComplete="tel" />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="10+ characters" autoComplete="new-password" />
              <Input label="Confirm password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>

            <div className="rounded-md border border-mist bg-haze/70 p-3">
              <Input label="First company (optional)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Skip if you'll create it later" hint="A company is the book of accounts for one legal entity" />
              <label className="mt-3 flex items-start gap-2 text-caption text-slate">
                <input type="checkbox" checked={seedSampleData} onChange={(e) => setSeedSampleData(e.target.checked)} className="mt-0.5" />
                <span>
                  <span className="text-obsidian">Pre-seed sample data</span> — 10 parties, 15 items, a few invoices, so you can explore reports immediately. Uncheck to start from a blank workspace.
                </span>
              </label>
            </div>

            {error && (
              <div className="rounded-md border border-crimson/20 bg-crimson/[0.05] px-3 py-2 text-body-sm text-crimson">{error}</div>
            )}

            <Button type="submit" loading={loading} size="lg" className="mt-2 w-full">
              {loading ? 'Setting up your workspace…' : 'Create workspace'}
            </Button>

            <div className="mt-2 flex items-center justify-between text-caption text-slate">
              <span>Press <KbdChip>Enter</KbdChip> to submit</span>
              <a href="/login" className="text-cobalt hover:underline">Already have an account?</a>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-caption text-cool max-w-md">
          By creating a workspace you agree to keep your data lawful + India-compliant.
          Owners get full control; you can add users + branches after sign-up.
        </p>

        <div className="mt-4 flex items-center gap-4 text-caption text-cool">
          <a href="/pricing" className="hover:text-obsidian">Pricing</a>
          <span>·</span>
          <a href="/status" className="hover:text-obsidian">Status</a>
          <span>·</span>
          <a href="/docs" className="hover:text-obsidian">Docs</a>
        </div>
      </div>
    </div>
  );
}
